import moment from 'moment-timezone';

import {
  AGE_OF_MAJORITY,
  calendarPickerRangeFormat,
  displayDateFormat,
  displayDateFormat_HH_MM,
  displayDateFormat_MMM_DD,
  ELAPSED_TIME_INVALID,
  LOCAL_DATE,
  MARKET_DATA_DATE,
  MARKET_DATA_TIME_AND_DATE,
  MARKET_DATA_TIME_AND_DATE_WITH_TZ,
  MARKET_DATA_TIME_AND_DATE_WITH_TZ2,
  MARKET_DATA_TIME_AND_DATE_WITH_TZ3,
  MARKET_DATA_TIME_AND_DATE2,
  MARKET_DATA_TIME_ZONE,
  SUCCESSION_DOWNTIME_END_NY_HOUR,
  SUCCESSION_DOWNTIME_END_NY_MINUTES,
  SUCCESSION_DOWNTIME_NY_FRIDAY_CLOSING_HOUR,
  SUCCESSION_DOWNTIME_START_NY_HOUR,
  SUCCESSION_DOWNTIME_START_NY_MINUTES,
  SUCCESSION_OPEN_NY_SUNDAY_NY_HOUR,
  SUCCESSION_OPEN_NY_SUNDAY_NY_MINUTES,
  UTC_MOMENT_FORMAT,
  utcISOTruncated,
} from '../constants/date-time.constants';
import lang from '../language';
import { MarketCountryCode } from '../models/fundamental/types';
import { MarketStateInstrumentSnapshot } from '../models/market-data/types';
import { ChartDataRange } from '../store/market-data/charting/types';
import MarketHoursState from '../store-util/MarketHoursState';

import { isNumber } from './DataHelpers';
import { isDateMarketSaturdayOrSunday, parseTimeToFloat } from './DateTimeUtils';
import * as MarketDataHelpers from './MarketDataHelpers';
import { calculateMarketState, getDateTime as getDateTimeFromTDZ } from './temporalDeadZoneFunctions';
import { isBoolean, isObjectLike, isString } from './typeGuards';
import {
  INVALID_DATE_STRING,
  INVALID_VALUE,
  INVALID_YEAR,
  MarketDataWorkingHours,
  MarketDataWorkingHoursAsNumber,
  MarketDataWorkingHoursAsString,
  NullableNumber,
  NullableString,
} from './types';

// Under the old Jest (CommonJS) runner, modules like 'moment-timezone' were mocked
// after this module loaded, so a static import would capture the real implementation
// and ignore the mock; resolving at call time picked the mock up. Under Vitest (ESM)
// `require` is unavailable, so this always falls back to the static import — tests
// pin the clock with `vi.setSystemTime` instead.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const require: any;
function getMomentTz() {
  try {
    const m = require('moment-timezone');
    return m?.default ?? m;
  } catch (_e) {
    return moment;
  }
}

// Same call-time resolution for 'moment' (see getMomentTz above).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getMoment() {
  try {
    const m = require('moment');
    return m?.default ?? m;
  } catch (_e) {
    // moment-timezone re-exports moment API; fall back to it
    return getMomentTz();
  }
}

const isDate = moment.isDate;
const isMoment = moment.isMoment;
const getDateTime = getDateTimeFromTDZ;

export { getDateTimeFromTDZ as getDateTime };

export enum ParseToType {
  utcForBackend,
  ForBackendDateOnly,
  dateObject,
  displayFormat
}
export enum FallbackToType {
  inputValue,
  todaysDate,
  invalidDateText
}

const {
  IS_MarketClosedNextDay,
  IS_PreMarket,
  IS_RegularHours,
  IS_AfterMarket,
  IS_MarketClosed,
} = MarketStateInstrumentSnapshot;

const isValidDate = (value: Date | moment.Moment | NullableString) => {
  if (!value || isBoolean(value)) return false;

  if (value instanceof Date && !isNaN(value.getTime())) {
    return true;
  }

  if (isMoment(value) && value.isValid()) {
    return true;
  }

  const momentValue = isMoment(value) ? value : moment(
    value,
    [
      utcISOTruncated,
      displayDateFormat,
      UTC_MOMENT_FORMAT,
      MARKET_DATA_TIME_AND_DATE,
      MARKET_DATA_TIME_AND_DATE_WITH_TZ,
      MARKET_DATA_TIME_AND_DATE_WITH_TZ2,
      MARKET_DATA_TIME_AND_DATE_WITH_TZ3,
      MARKET_DATA_TIME_AND_DATE2,
    ],
    true,
  );

  return momentValue.isValid();
};

export const toMomentDate = (value?: NullableString | Date | moment.Moment | null): moment.Moment | undefined => {
  if (!value) return undefined;

  if (isMoment(value)) {
    return (value as moment.Moment).clone();
  }

  if (value instanceof Date) {
    return moment(value);
  }

  const parsedMoment = moment(value);
  return parsedMoment.isValid() ? parsedMoment : undefined;
};

export const getMaxAllowedDateByAge = (ageLimit = AGE_OF_MAJORITY): moment.Moment => moment().subtract(ageLimit, 'years').startOf('day');

export const isUnderAge = (
  value?: NullableString | Date | moment.Moment | null,
  ageLimit = AGE_OF_MAJORITY,
): boolean => {
  const parsedDate = toMomentDate(value);
  if (!parsedDate) {
    return false;
  }

  return moment().diff(parsedDate, 'years', true) < ageLimit;
};

export const isValidDateForYear = (value: string | number | null | undefined) => {
  if (value == null) return false;
  if (value === INVALID_YEAR) return false;
  if (value === INVALID_VALUE) return false;
  if (value === Number.POSITIVE_INFINITY) return false;
  if (value === Number.NEGATIVE_INFINITY) return false;
  if (isObjectLike(value)) return false;
  if (isBoolean(value)) return false;
  if (Array.isArray(value)) return false;
  if (isNumber(value) && (value as number) <= 0) return false;
  if (!`${value}`.match(/^[1-9][0-9]{3}/)) return false;

  return true;
};

export const isValidYear = (value: string | number | null | undefined) => (
  value !== INVALID_YEAR
  && value !== INVALID_VALUE
  && value !== Number.POSITIVE_INFINITY
  && value !== Number.NEGATIVE_INFINITY
  && value
  && Number(value)
  && Number(value) > 0
  && (isString(value) || isNumber(value))
  && !isObjectLike(value)
);

const determineFallback = (
  value: string | Date | null,
  parseTo: ParseToType,
  fallBackTo: FallbackToType | string,
): string | Date => {
  if (typeof fallBackTo === 'string') {
    if (isValidDate(fallBackTo)) {
      return determineReturnValue(fallBackTo, parseTo);
    }
    return 'invalid date && fallback date';
  }

  if (fallBackTo === FallbackToType.inputValue && value) {
    return value;
  } if (fallBackTo === FallbackToType.todaysDate) {
    return determineReturnValue(moment.utc().format(utcISOTruncated), parseTo);
  }
  return 'invalid date';
};

const determineReturnValue = (
  value: string | Date | null,
  parseTo: ParseToType,
): string | Date => {
  if (parseTo === ParseToType.utcForBackend) {
    return moment.utc(value, [ utcISOTruncated, displayDateFormat, UTC_MOMENT_FORMAT ]).format(utcISOTruncated);
  } if (parseTo === ParseToType.ForBackendDateOnly) {
    return moment.utc(value, [ utcISOTruncated, displayDateFormat, UTC_MOMENT_FORMAT ]).format(UTC_MOMENT_FORMAT);
  } if (parseTo === ParseToType.displayFormat) {
    return moment.utc(value, [ utcISOTruncated, displayDateFormat, UTC_MOMENT_FORMAT ]).format(displayDateFormat);
  }
  return moment.utc(value, [ utcISOTruncated, displayDateFormat, UTC_MOMENT_FORMAT ]).toDate();
};

/**
 *
 * @param value string | Date | null
 * Value to be converted. It accepts all formats we use: Date object, displayDateFormat, utcISOTruncated
 *
 * @param parseTo ParseToType
 * Value can be parsed to any format part of our suite as above.
 *
 * @param fallBackTo FallbackToType | string
 * When Value is invalid it returns this parameter.
 * String value will return itself with respect to the parseTo type
 *
 * @returns
 * 1. Valid string formated value
 * 2. Valid Date object
 * 3. When Value is invalid returns fallBack with respect to its type or if not specified, it uses its default.
 */
export const parseDateTo = (
  value: string | Date | null,
  parseTo: ParseToType = ParseToType.dateObject,
  fallBackTo: FallbackToType | string = FallbackToType.todaysDate,
): string | Date => {
  if (!isValidDate(value)) {
    return determineFallback(value, parseTo, fallBackTo);
  }
  return determineReturnValue(value, parseTo);
};

/**
 * Returns a formatted string of date time value  of now or `customDate` if given.
 * Using `utcISOTruncated` to format as string or a custom format if `format` operation given.
 * @param isUtc If `true` - using time in UTC. If no customDate - current time is in UTC, if customDate present - parsed as UTC.
 * @param operations A list of operations to apply on now date and time
 * @param operationArgs A list of arguments per operation from `operations` list - currently based on momenttz
 *        (see the switch in forEach or the DataHelpers.test.ts file for example usage)
 * @param customDate Custom date to use instead of now
 */

/**
 *
 * @param timeInSec Time in seconds. Two ways to generate - moment().unix() or Math.trunc(new Date().getTime() / 1000)
 * @param nowInSec If custom now is needed - value must be in seconds as well.
 * @deprecated use calculateTimeRemaining instead of this for the simple calculation
 */
export function calculateElapsedTime(
  timeInSec: NullableNumber,
  nowInSec?: NullableNumber,
  ZERO: NullableString = ELAPSED_TIME_INVALID,
  INVALID: NullableString = ELAPSED_TIME_INVALID,
) {
  if (!isNumber(timeInSec) || timeInSec! < 0) return INVALID ?? ELAPSED_TIME_INVALID;

  const current = nowInSec == null ? moment().unix() : nowInSec;
  const diff = (timeInSec! - current);
  if (diff < 0) return INVALID ?? ELAPSED_TIME_INVALID;
  if (diff === 0) return ZERO ?? ELAPSED_TIME_INVALID;

  const duration = moment.duration(diff, 'seconds');
  const seconds = duration.seconds();
  const minutes = duration.minutes();
  const hours = duration.hours();
  const days = duration.days();
  const asDays = duration.asDays();

  const f = (n: number) => n.toString().padStart(2, '0');
  if (asDays > 1) return formatTimeRemaining(days, hours, minutes, seconds);

  let result = `${f(minutes)}:${f(seconds)}`;
  if (asDays === 1) result = `${f(duration.asHours())}:${result}`;
  else if (hours > 0) result = `${f(hours)}:${result}`;
  return result;
}

//  *** Date section ***
// TODO: Remove these after synced [WEB] with DateTimeHelpers/parseDateTo ART-942

/**
 *
 * @param value In the format 'DD MM, YYYY'
 */
export function parseDateToUTC(value: string) {
  return parseDateTo(value, ParseToType.utcForBackend);
}

/**
 *
 * @param date Date()
 * Converts Date object to our date display format.
 * It accepts both 'YYYY-MM-DDT00:00:00' and 'YYYY-MM-DDT00:00.000Z' as either Date | string
 */
export const convertDateToFormatedString = (
  date: Date | string | null,
  fallbackValue?: string,
): string => parseDateTo(
  date,
  ParseToType.displayFormat,
  fallbackValue || FallbackToType.todaysDate,
) as string;

/**
 * Get previous trading day from now or based on date.
 */
export const getPreviousWorkday = (
  date: NullableString | moment.Moment,
  countryCode: string = MarketCountryCode.UnitedStates,
): moment.Moment | null => {
  const { marketHours } = MarketHoursState.get(countryCode as MarketCountryCode);
  const timeZone = marketHours.timeZone || MARKET_DATA_TIME_ZONE;

  let current;
  if (isMoment(date)) current = date;
  else {
    current = (
      date
        ? moment.tz(date, timeZone)
        : moment.tz(timeZone)
    );
  }

  if (!current.isValid()) {
    console.error(`[DateTimeHelpers] calculateTimeToNextMarketState - invalid Date - '${date}'`);
    return null;
  }

  do {
    current.subtract(1, 'day');
  } while (!isDateMarketDataWorkingDay(current.format('YYYY-MM-DD'), true, countryCode));

  return current;
};

/**
 * Get next trading day from now or based on date.
 */
export const getNextWorkday = (
  date: NullableString | moment.Moment,
  countryCode: string = MarketCountryCode.UnitedStates,
): moment.Moment | null => {
  const { marketHours } = MarketHoursState.get(countryCode as MarketCountryCode);
  const timeZone = marketHours.timeZone || MARKET_DATA_TIME_ZONE;

  let current = (
    date
      ? moment.tz(date, timeZone)
      : moment.tz(timeZone)
  );

  if (!current.isValid()) {
    console.error(`[DateTimeHelpers] calculateTimeToNextMarketState - invalid Date - '${date}'`);
    return null;
  }

  do {
    current.add(1, 'day');
  } while (!isDateMarketDataWorkingDay(current.format('YYYY-MM-DD'), true, countryCode));

  return current;
};

export function datesDifference(
  from: string,
  to: string,
  difference: 'days' | 'minutes' | 'seconds' | 'weeks' | 'months' | 'years' = 'days',
) {
  const a = moment(from);
  const b = moment(to);

  return b.diff(a, difference);
}

/**
 * Calculates diff in seconds, milliseconds etc. to next market states - pre market and regular - when applicable.
 * It considers the current market state. If regular hours - skips calculations and returns nulls for diffs.
 * @param dateInUTC Custom time if needed - in UTC
 * @param diffType The unit type that the diff will be presented as
 * @param asFloat If set to 'true' returns as floats, for example 1.6 if diffType is `diffType` and diff value is 1.6 seconds
 * @param countryCode The country code to use for market hours
 */
export function calculateTimeToNextMarketState(
  dateInUTC: NullableString,
  diffType: 's' | 'ms' | 'h' | 'd' | 'milliseconds' | 'seconds' | 'hours' | 'days',
  asFloat: boolean,
  countryCode: string = MarketCountryCode.UnitedStates, // TODO: replace this function from HW. ACE-591
) {
  const { convertToMarketState } = MarketDataHelpers;

  const { marketHours } = MarketHoursState.get(countryCode as MarketCountryCode);
  const timeZone = marketHours.timeZone || MARKET_DATA_TIME_ZONE;

  let now = (
    dateInUTC
      ? getMomentTz().tz(dateInUTC, timeZone)
      : getMomentTz().tz(timeZone)
  );

  if (!now.isValid()) {
    console.error(`[DateTimeHelpers] calculateTimeToNextMarketState - invalid Date - '${dateInUTC}'`);
    return {
      nextPreMarketMoment: null,
      nextRegularMoment: null,
      marketState: null,
    };
  }

  const preMarket = parseTimeToFloat(marketHours.preMarketStart);
  const open = parseTimeToFloat(marketHours.open);
  const dateTimeInUTC = now.utc().toISOString();
  const instrumentSnapshotMarketState = calculateMarketState(dateTimeInUTC, countryCode);

  let nextPreMarketMoment: moment.Moment | null | undefined = null;
  let nextRegularMoment: moment.Moment | null | undefined = null;

  switch (instrumentSnapshotMarketState) {
    case IS_MarketClosedNextDay: {
      const dataDay = isDateMarketDataWorkingDay(
        now.utc(),
        false,
        countryCode,
      ) ? now : getNextWorkday(now.utc(), countryCode);
      // pre market
      nextPreMarketMoment = dataDay?.clone().tz(timeZone).set(timeAsFloatToMomentSetterParam(preMarket));
      // regular
      nextRegularMoment = dataDay?.clone().tz(timeZone).set(timeAsFloatToMomentSetterParam(open));
      break;
    }
    case IS_PreMarket:
      // pre market
      // Pre market timer calculation is not needed in this state since all cases use `nextRegularMoment` (market orders message timer, short selling popup timer etc.).
      // regular
      nextRegularMoment = now.clone().tz(timeZone).set(timeAsFloatToMomentSetterParam(open));
      break;
    case IS_RegularHours:
      // > No timer calculation is needed in this state since all orders are possible.
      break;
    case IS_AfterMarket: {
      const nextWorkingDayMoment = getNextWorkday(now.utc(), countryCode);
      // pre market
      // Pre market timer calculation is not needed in this state since the only valid case here (short selling popup timer) uses `nextRegularMoment`.
      // regular
      nextRegularMoment = (
        nextWorkingDayMoment?.clone().tz(timeZone).set(timeAsFloatToMomentSetterParam(open))
      );
      break;
    }
    case IS_MarketClosed: {
      const nextWorkingDayMoment = getNextWorkday(now.utc(), countryCode);
      // pre market
      nextPreMarketMoment = (
        nextWorkingDayMoment?.clone().tz(timeZone).set(timeAsFloatToMomentSetterParam(preMarket))
      );
      // regular
      nextRegularMoment = (
        nextWorkingDayMoment?.clone().tz(timeZone).set(timeAsFloatToMomentSetterParam(open))
      );
      break;
    }
    default:
      break;
  }
  const nextPreMarket = nextPreMarketMoment?.diff(now, diffType, asFloat) ?? null;
  const nextRegular = nextRegularMoment?.diff(now, diffType, asFloat) ?? null;

  return { nextPreMarket, nextRegular, marketState: convertToMarketState(instrumentSnapshotMarketState) };
}

/**
 * Calculates the time remaining until the succession OMS open based on New York time (Eastern Time).
 *
 * Rules:
 * - Closed all day Saturday.
 * - Closed all day Sunday until 19:45 NY time.
 * - Closed on Friday after 20:00.
 * - Opens at 19:45 NY time on Sunday.
 * - Weekday downtime occurs every day from 02:30 AM to 02:45 AM NY time.
 *   - During this period, it is closed and will reopen at 02:45 AM.
 * This function calculates the time remaining until it opens,
 * depending on the current day of the week and time in New York.
 */
export function calculateTimeToSuccessionOMSOpen() {
  const mtz = getMomentTz();
  const now = mtz.tz(MARKET_DATA_TIME_ZONE); // New York time
  const dayOfWeek = now.day();
  const nyHours = now.hours();
  const nyMinutes = now.minutes();

  let nextMarketOpen: Date | null = null;

  if (dayOfWeek === 5 && nyHours >= SUCCESSION_DOWNTIME_NY_FRIDAY_CLOSING_HOUR) {
    // Market opens on Sunday 19:45 NY time
    nextMarketOpen = now.clone().add(2, 'days').set({ hour: SUCCESSION_OPEN_NY_SUNDAY_NY_HOUR, minute: SUCCESSION_OPEN_NY_SUNDAY_NY_MINUTES, second: 0, millisecond: 0 }).toDate();
  }
  if (dayOfWeek === 6) { // Saturday
    // Market opens on Sunday 19:45 NY time
    nextMarketOpen = now.clone().add(1, 'days').set({ hour: SUCCESSION_OPEN_NY_SUNDAY_NY_HOUR, minute: SUCCESSION_OPEN_NY_SUNDAY_NY_MINUTES, second: 0, millisecond: 0 }).toDate();
  } else if (dayOfWeek === 0 && (nyHours < SUCCESSION_OPEN_NY_SUNDAY_NY_HOUR
    || (nyHours === SUCCESSION_OPEN_NY_SUNDAY_NY_HOUR && nyMinutes < SUCCESSION_OPEN_NY_SUNDAY_NY_MINUTES))) {
    // It's Sunday before 19:45 NY time, the market opens at 19:45 NY time
     
    nextMarketOpen = now.clone().set({ hour: SUCCESSION_OPEN_NY_SUNDAY_NY_HOUR, minute: SUCCESSION_OPEN_NY_SUNDAY_NY_MINUTES, second: 0, millisecond: 0 }).toDate();
  } else if (nyHours === SUCCESSION_DOWNTIME_START_NY_HOUR && nyMinutes >= SUCCESSION_DOWNTIME_START_NY_MINUTES
    && nyMinutes < SUCCESSION_DOWNTIME_END_NY_MINUTES) {
    // Weekday Downtime 02:30-02:45 NY time
     
    nextMarketOpen = now.clone().set({ hour: SUCCESSION_DOWNTIME_END_NY_HOUR, minute: SUCCESSION_DOWNTIME_END_NY_MINUTES, second: 0, millisecond: 0 }).toDate();
  }

  if (!nextMarketOpen) {
    return { nextMarketOpen: 0 };
  }

  const timeDiffMilliseconds = nextMarketOpen.getTime() - now.toDate().getTime();
  const timeDiff = timeDiffMilliseconds / 1000;

  return {
    nextMarketOpen: timeDiff,
  };
}

export const timezoneDiffUTCMarketHours = (): number => {
  const now = moment.tz(MARKET_DATA_TIME_ZONE);
  const tz = now.format('Z');
  const timeDiff = (-1) * timeToFloat(tz.substr(0, 6))!;
  return timeDiff;
};

/**
 * @param date The date to parse
 * @param type If `ny` - NY time used
 */
function parseDateForWorkingHours(date: NullableString | Date | moment.Moment, timezone: string) {
  if (!date) return INVALID_DATE_STRING;

  if (isMoment(date)) return date.format();
  if (isDate(date)) return moment(date).format();
  if (isString(date)) {
    if (date.length < 20) { // no TZ in date string
      return moment.tz(date.substr(0, 10), timezone).format();
    }

    return moment(date).format();
  }

  return INVALID_DATE_STRING;
}

/**
 * Checks if the day is a working or a holiday
 * Ignores time by default
 */
export function isDateMarketDataWorkingDay(
  dateInUtc: Date | moment.Moment | string,
  ignoreTime: boolean = false,
  countryCode: string = MarketCountryCode.UnitedStates,
): boolean | null {
  if (!isValidDate(dateInUtc)) {
    console.error(`[DateTimeHelpers] isDateMarketDataWorkingDay - invalid input date '${dateInUtc}' (${typeof dateInUtc})`);
    return null;
  }

  const { nonWorkingDays, marketHours } = MarketHoursState.get(countryCode as MarketCountryCode);
  const timeZone = marketHours.timeZone || MARKET_DATA_TIME_ZONE;

  const theDate = (
    ignoreTime
      ? parseDateForWorkingHours(dateInUtc, timeZone)
      : moment.utc(dateInUtc).tz(timeZone).format()
  );
  const asString = theDate.substr(0, 10);
  const isHoliday = nonWorkingDays.some(
    day => day.date === asString && !day.halfDay,
  );
  const marketDataDate = moment.tz(asString, timeZone);
  const isWeekend = isDateMarketSaturdayOrSunday(marketDataDate);

  return !isHoliday && !isWeekend;
}

// TODO: Rename to timeAsFakeFloat or mark otherwise tha it is using fake float conversion (for example 14:30 becomes 14.3 not 14.5)
export function timeToFloat(time: string | moment.Moment | Date): NullableNumber {
  if (isString(time)) {
    let buff = time.replace(/[.]*/, '');
    if ((buff.match(/[:]/g) ?? []).length > 1) {
      buff = buff.substring(0, buff.lastIndexOf(':'));
    }
    buff = buff.replace(/:00/, '');
    buff = buff.replace(':', '.');
    return Number(buff);
  }

  if (isMoment(time)) {
    return time.hour() + time.minute() / 100;
  }

  if (isDate(time)) {
    return time.getUTCHours() + time.getUTCMinutes() / 100;
  }

  return null;
}

/**
 * Returns an object to use on moment.set(object) to reset it to a certain time.
 * @param timeAsFloat Float value for hours and minutes in a 'fake float' format, for example - 12.3 (meaning 12:30), 11.45 (meaning 11:45)
 */
export function timeAsFloatToMomentSetterParam(timeAsFloat: number) {
  return {
    hour: Math.trunc(timeAsFloat),
    minute: Math.round(100 * (timeAsFloat % 1)),
    second: 0,
    ms: 0,
  };
}

/**
 * Used for preparing year data for reading from server.
 * Please use `yearToDate` to read
 * @param date Date string YYYY-MM-DD[T]HH:mm or year as number
 * @returns Year as number
 */
export const dateToYear = (date: string | number | null | undefined): number => {
  if (!isValidDateForYear(date)) return INVALID_YEAR;
  if (isString(date)) return Number(date.substr(0, 4));
  if (isNumber(date)) return Number(date);

  return INVALID_YEAR;
};

export const dateToMonth = (date: string | number | null | undefined): number => {
  if (!isValidDateForYear(date)) return INVALID_YEAR;
  if (isString(date)) return Number(date.substr(5, 2));
  if (isNumber(date)) return Number(date);

  return INVALID_YEAR;
};

export const checkChartRangeHasTime = (range: ChartDataRange) => [ '1D', '1W', '1M' ].includes(range);
export const formatChartingDateTime = (dateAndTime: string, range: ChartDataRange, tz: 'local' | 'new-york' | 'utc') => {
  if (!isValidDate(dateAndTime)) return INVALID_DATE_STRING;

  const hasTime = checkChartRangeHasTime(range!);
  let format = MARKET_DATA_DATE;
  if (hasTime) {
    if (tz === 'utc') {
      format = MARKET_DATA_TIME_AND_DATE;
    } else {
      format = MARKET_DATA_TIME_AND_DATE;
    }
  }

  if (!hasTime) return moment.utc(dateAndTime).format(format);

  switch (tz) {
    case 'local':
      return moment.utc(dateAndTime!).tz(MARKET_DATA_TIME_ZONE).local().format(format);
    case 'new-york':
      return moment.utc(dateAndTime!).tz(MARKET_DATA_TIME_ZONE).format(format);
    default:
      return moment.utc(dateAndTime!).format(format);
  }
};

export const formatNetComponentsDate = (dateAndTime: string) => {
  if (!isValidDate(dateAndTime)) return INVALID_DATE_STRING;
  return moment(dateAndTime).format(calendarPickerRangeFormat);
};

/**
 * Does not take TZ into account
 */
export function isDateEqual(dateOne: NullableString, dateTwo: NullableString) {
  return dateOne?.substr(0, 10) === dateTwo?.substr(0, 10);
}

export const leapYear = (year: string) => moment(year).isLeapYear();

export const formatInboxreceptionTime = (timestamp: number) => {
  const oneWeekAgoTimestamp = +moment().subtract(1, 'w');
  const isOlderThanAWeek: boolean = oneWeekAgoTimestamp > timestamp;
  if (isOlderThanAWeek) {
    return moment(timestamp).format(displayDateFormat_MMM_DD);
  }
  return `${moment(timestamp).toNow(true)} ago`;
};

export const isBeforeDate = (
  value: Date | string | undefined,
  minDate: Date | string | undefined,
): boolean => moment(value).isBefore(minDate);

export const earningsCalendarInitialDate = () => (
  isDateMarketDataWorkingDay(new Date(), true, MarketCountryCode.UnitedStates)
    ? new Date()
    : getNextWorkday(null, MarketCountryCode.UnitedStates)?.toDate());

export const tradeHistoryCalendarInitialMaxDate = () => (
  isDateMarketDataWorkingDay(new Date(), true, MarketCountryCode.UnitedStates)
    ? new Date()
    : getPreviousWorkday(undefined, MarketCountryCode.UnitedStates)?.toDate());

export const getMillisecondsToSyncWithClock = () => 1000 - (new Date().getTime() % 1000);

export const formatUTCdateToLocalDate = ((date: string | undefined) => {
  if (date) {
    return getDateTime(
      true,
      [ 'format', 'local' ],
      [ [ `${LOCAL_DATE} \u2022 ${displayDateFormat_HH_MM}` ],
        [] ],
      new Date(`${date}Z`),
    );
  }

  return undefined;
});

/**
 * @param text string
 * @returns string date or undefined
 */
export const extractDateFromString = (text: string) => {
  const date = text.match(/\d{2}(\/)\d{2}(\/)\d{4} \d{2}:\d{2}/g); // Match format - 12/22/2022 12:45

  return date?.toString();
};

export const addDateAndFormatToMarketDataDate = (amount: number, unit: 'years' | 'months' | 'days'): string => {
  const m = getMoment();
  return m().add(amount, unit).format(MARKET_DATA_DATE);
};

/**
 * Formats a given time in seconds into a human-readable string.
 * The function handles various scenarios:
 * - If the time is less than or equal to 0, it returns "00:00".
 * - If the time represents exactly 24 hours, it returns "24:00:00".
 * - If the time spans more than 24 hours, it returns the number of full days (e.g., "2 days").
 * - Otherwise, it formats the time as "HH:MM:SS" or "MM:SS" depending on whether there are hours.
 * @param {number} seconds - The total time in seconds.
 */
export function formatSuccessionTime(seconds: number) {
  if (seconds <= 0) return '00:00';

  // Calculate the total hours, minutes, and seconds
  const totalHours = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  // Calculate the number of full days and the remaining hours
  const d = Math.floor(totalHours / 24);
  const h = totalHours % 24;

  // Helper function to pad single digit numbers with a leading zero
  const f = (n: number) => n.toString().padStart(2, '0');

  // Ensure the condition for exactly 24:00:00
  if (d === 1 && h === 0 && m === 0 && s === 0) {
    return '24:00:00';
  }

  // Return the number of days if more than 24 hours
  if (d > 0) {
    return `${d} day${d > 1 ? 's' : ''}`;
  }

  // Format the result as "MM:SS" or "HH:MM:SS" depending on whether there are hours
  let result = `${f(m)}:${f(s)}`;
  if (h > 0) {
    result = `${f(h)}:${result}`;
  }

  return result;
}

export const formatTimeRemaining = (days: number, hours: number, minutes: number, seconds: number): string => {
  if (days < 1) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return days === 1 ? `${lang.commonCalendarDay(1)}` : `${lang.commonCalendarDays(days)}`;
};

export const formatTimeRemainingHours = (days: number, hours: number, minutes: number, seconds: number): string => {
  if (days > 1) {
    return '';
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Calculates the time remaining from a given start date.
 *
 * @param {string} startDate - The start date to calculate the time remaining from.
 * @param {(value: boolean) => void} setHasNoRemainingTime - Callback to set whether there is no remaining time.
 * @param {(value: number) => void} setDays - Callback to set the remaining days.
 * @param {(value: number) => void} setHours - Callback to set the remaining hours.
 * @param {(value: number) => void} setMinutes - Callback to set the remaining minutes.
 * @param {(value: number) => void} setSeconds - Callback to set the remaining seconds.
 */
export const calculateTimeRemaining = (
  startDate: string,
  setHasNoRemainingTime: (value: boolean) => void,
  setDays?: (value: number) => void,
  setHours?: (value: number) => void,
  setMinutes?: (value: number) => void,
  setSeconds?: (value: number) => void,
  setIsRemainingTimeZero?: (value: boolean) => void,
) => {
  const now = moment().utc();
  const start = moment(startDate);
  const diff = moment.duration(start.diff(now));

  const days = Math.floor(diff.asDays());
  const hours = diff.hours();
  const minutes = diff.minutes();
  const seconds = diff.seconds();
  const noRemainingTime = days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0;

  if (setDays) setDays(days);
  if (setHours) setHours(hours);
  if (setMinutes) setMinutes(minutes);
  if (setSeconds) setSeconds(seconds);
  if (setIsRemainingTimeZero) setIsRemainingTimeZero(days === 0 && hours === 0 && minutes === 0 && seconds === 0);
  setHasNoRemainingTime(noRemainingTime);
};

/**
 * Returns market working hours for a given date.
 * @param date - the date to check
 * @param asUTC - when true, return times in UTC format (as strings)
 * @param asNumbers - when false, return times as number floats; when true (default), return as strings
 */
export function getMarketWorkingHours(
  date: Date | moment.Moment | string,
  asUTC = false,
  asNumbers = true,
): MarketDataWorkingHours {
  const { marketHours } = MarketHoursState.get(MarketCountryCode.UnitedStates);
  const timeZone = marketHours.timeZone || MARKET_DATA_TIME_ZONE;

  const convertToUTC = (timeStr: string): string => {
    if (!timeStr) return '';
    return moment.tz(`${moment(date).format('YYYY-MM-DD')} ${timeStr}`, timeZone).utc().format('HH:mm');
  };

  if (asUTC) {
    return {
      startTime: convertToUTC(marketHours.open),
      endTime: convertToUTC(marketHours.close),
      endAfter: convertToUTC(marketHours.afterMarketEnd),
    } as MarketDataWorkingHoursAsString;
  }

  if (!asNumbers) {
    return {
      startTime: parseTimeToFloat(marketHours.open) ?? 0,
      endTime: parseTimeToFloat(marketHours.close) ?? 0,
      endAfter: parseTimeToFloat(marketHours.afterMarketEnd),
    } as MarketDataWorkingHoursAsNumber;
  }

  return {
    startTime: marketHours.open,
    endTime: marketHours.close,
    endAfter: marketHours.afterMarketEnd,
  } as MarketDataWorkingHoursAsString;
}
